import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HighlightCards,
  TextField,
  Select,
  Button,
  Tag,
  ButtonMenu,
} from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { generateAvatarColor } from "../../../../../../../utils/avatarUtils";
import { useToast } from "../../../../../../../components/toast";
import { useDepartmentUsers } from "../../context";
import {
  useUsersForDepartments,
  useAssignUsersToGroup,
} from "../../../../context";
import { getUserStatusListFilter } from "../../../../../../../services/users";
import { useDebounce, useStore } from "../../../../../../../hooks";
import { formatStatusLabel } from "../../../../../../../utils/uiUtils";
import RemoveUserModal from "./components/RemoveUserModal";
import AddUserModal from "./components/AddUserModal";
import AddUserModalStore from "./components/AddUserModal/AddUserModalStore";
import ResourcesStore from "./components/AddUserModal/ResourcesStore";
import {
  UsersContainer,
  HighlightCardsContainer,
  HighlightCardColumn,
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  DropdownWrapper,
  AvatarNameContainer,
  NameCell,
  GroupsContainer,
} from "./Users.styled";
import UsersTableSection from "./components/UsersTableSection";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toISOString().split("T")[0];
  } catch {
    return "—";
  }
};

const Users = ({ departmentId, groupUuid: groupUuidProp }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { departmentsStore } = useStore();

  const { users, meta, loading, error, isEmpty, fetchUsers } = useDepartmentUsers();

  const {
    autocompleteUsers,
    loading: loadingUsersForModal,
    fetchUsers: fetchUsersForModal,
    searchUsers,
    invalidateUserListCache,
  } = useUsersForDepartments();

  const { assignUsers, assigning: isAssigningUsers } = useAssignUsersToGroup();
  const [statusOptions, setStatusOptions] = useState([]);

  const department = departmentsStore.currentDepartment;

  const departmentUuid = useMemo(() => {
    if (departmentId) {
      return departmentId;
    }

    if (department) {
      return (
        department?.department_uuid ||
        department?.departmentUuid ||
        department?.department_id ||
        department?.id
      );
    }

    return null;
  }, [departmentId, department]);

  const [localUsers, setLocalUsers] = useState([]);

  const [addUserModalStore] = useState(() => new AddUserModalStore());
  const [resourcesStore] = useState(() => new ResourcesStore());
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [modalSearchKeyword, setModalSearchKeyword] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filtersKey, setFiltersKey] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const debouncedSearchKeyword = useDebounce(searchKeyword, 700);

  const effectiveSearchKeyword =
    searchKeyword.trim() === "" ? "" : debouncedSearchKeyword;
  const debouncedModalSearchKeyword = useDebounce(modalSearchKeyword, 500);

  useEffect(() => {
    if (!departmentUuid) {
      return;
    }

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (!uuidRegex.test(departmentUuid)) {
      return;
    }

    fetchUsers({
      department_uuid: departmentUuid,
      page: currentPage,
      page_size: itemsPerPage,
      order_by: ["created_at"],
      order_direction: ["DESC"],
      search: effectiveSearchKeyword.trim()
        ? { keyword: effectiveSearchKeyword.trim() }
        : undefined,
    });
  }, [
    departmentUuid,
    currentPage,
    itemsPerPage,
    effectiveSearchKeyword,
    fetchUsers,
  ]);

  useEffect(() => {
    if (!isAddUserModalOpen) return;
    if (debouncedModalSearchKeyword !== modalSearchKeyword) return;
    searchUsers(debouncedModalSearchKeyword);
  }, [isAddUserModalOpen, debouncedModalSearchKeyword, modalSearchKeyword, searchUsers]);

  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  useEffect(() => {
    if (error && !loading) {
      const errorMessage = error?.message || 
                          error?.response?.data?.msgCode?.[0]?.description ||
                          error?.response?.data?.msg_code?.[0]?.description ||
                          "Failed to load department users. Please try again.";
      
      toast.warning({
        title: "Error loading users",
        message: errorMessage,
      });
    }
  }, [error, loading, toast]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await getUserStatusListFilter({ page_size: 50 });
        if (response?.body) {
          const options = (response.body || []).map((item) => {
            const status = item.status || "";
            return {
              value: status,
              label: formatStatusLabel(status),
            };
          });
          setStatusOptions(options);
        }
      } catch (error) {
        console.error("Error fetching status options:", error);
      }
    };

    fetchStatuses();
  }, []);

  const filteredUsers = useMemo(() => {
    let filtered = [...localUsers];

    if (selectedRole) {
      filtered = filtered.filter((user) => user.memberRole === selectedRole);
    }

    if (selectedStatus) {
      filtered = filtered.filter((user) => {
        const userStatus = user.status?.toUpperCase();
        return userStatus === selectedStatus.toUpperCase();
      });
    }

    return filtered;
  }, [localUsers, selectedRole, selectedStatus]);

  const statistics = useMemo(() => {
    const totalUsers = meta?.totalItems || filteredUsers.length;
    const activeUsers = filteredUsers.filter((u) => {
      const status = u.status?.toUpperCase();
      return status === "ACTIVE" || status === "active";
    }).length;
    const pendingUsers = filteredUsers.filter((u) => {
      const status = u.status?.toUpperCase();
      return status === "PENDING" || status === "pending";
    }).length;
    const adminUsers = filteredUsers.filter(
      (u) => u.memberRole === "Department Admin" || u.memberRole === "Platform Admin"
    ).length;

    return {
      totalUsers,
      activeUsers,
      pendingUsers,
      adminUsers,
    };
  }, [filteredUsers, meta?.totalItems]);

  const paginatedUsers = filteredUsers;

  const handleSearchChange = useCallback((value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleRoleChange = useCallback((value) => {
    const roleValue =
      typeof value === "object" && value !== null ? value.value : value;
    setSelectedRole(roleValue);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((value) => {
    const statusValue =
      typeof value === "object" && value !== null ? value.value : value;
    setSelectedStatus(statusValue);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchKeyword("");
    setSelectedRole(null);
    setSelectedStatus(null);
    setFiltersKey((prev) => prev + 1);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      (searchKeyword && searchKeyword.trim() !== "") ||
      selectedRole !== null ||
      selectedStatus !== null
    );
  }, [searchKeyword, selectedRole, selectedStatus]);

  const handleAddUser = useCallback(() => {
    addUserModalStore.itemsToShare = [
      { type: "user", name: "Department Users" },
    ];
    addUserModalStore.currentSelectionType = "document";
    addUserModalStore.shouldShowGranteesList = false;
    setModalSearchKeyword("");
    setIsAddUserModalOpen(true);
    fetchUsersForModal({ page_size: 50 });
  }, [addUserModalStore, fetchUsersForModal]);

  const handleCloseAddUserModal = useCallback(() => {
    setIsAddUserModalOpen(false);
    setModalSearchKeyword("");
    setTimeout(() => {
      addUserModalStore.closeModal();
    }, 300);
  }, [addUserModalStore]);

  const handleModalSearchUsers = useCallback((keyword) => {
    setModalSearchKeyword(typeof keyword === "string" ? keyword : "");
  }, []);

  const handleAddToPending = useCallback(() => {
    const result = addUserModalStore.addToPendingGrantees();
    if (result?.message) {
      toast[result.variant || "warning"]({
        title: result.title,
        message: result.message,
      });
    }
  }, [addUserModalStore, toast]);

  const handleShareAll = useCallback(async () => {
    if (addUserModalStore.isLoadingSharing || isAssigningUsers) {
      return;
    }

    const usersCount = addUserModalStore.pendingGrantees.length;

    if (usersCount === 0) {
      return;
    }

    const userUuids = addUserModalStore.pendingGrantees.map(
      (grantee) => grantee.grantee_uuid
    );

    if (!departmentUuid) {
      toast.error({
        title: "Error",
        message: "Department UUID is required to add users.",
      });
      return;
    }

    try {
      addUserModalStore.isLoadingSharing = true;

      await assignUsers(userUuids, departmentUuid);

      if (departmentUuid) {
        await fetchUsers({
          department_uuid: departmentUuid,
          page: currentPage,
          page_size: itemsPerPage,
          order_by: ["created_at"],
          order_direction: ["DESC"],
          search: effectiveSearchKeyword.trim()
            ? { keyword: effectiveSearchKeyword.trim() }
            : undefined,
        });
      }

      invalidateUserListCache();
      setIsAddUserModalOpen(false);

      setTimeout(() => {
        addUserModalStore.closeModal();
      }, 300);

      setTimeout(() => {
        toast.success({
          title: "Users added",
          message: `${usersCount} ${
            usersCount === 1 ? "user has" : "users have"
          } been successfully added to the department.`,
        });
      }, 350);
    } catch (error) {
      addUserModalStore.isLoadingSharing = false;

      const errorMessage =
        error?.response?.data?.msg_code?.[0]?.description ||
        error?.response?.data?.msgCode?.[0]?.description ||
        error?.message ||
        "Failed to add users. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      addUserModalStore.isLoadingSharing = false;
    }
  }, [
    addUserModalStore,
    toast,
    isAssigningUsers,
    departmentUuid,
    assignUsers,
    fetchUsers,
    currentPage,
    itemsPerPage,
    effectiveSearchKeyword,
    invalidateUserListCache,
  ]);

  const handleUpdatePendingPermission = useCallback(
    (granteeUuid, newPermission) => {
      addUserModalStore.updatePendingGranteePermission(
        granteeUuid,
        newPermission
      );
    },
    [addUserModalStore]
  );

  const handleRemovePending = useCallback(
    (granteeUuid) => {
      addUserModalStore.removePendingGrantee(granteeUuid);
    },
    [addUserModalStore]
  );

  const handleUpdateGranteePermission = useCallback(() => {
    // TODO: Implement update grantee permission
  }, []);

  const handleRemoveGrantee = useCallback(() => {
    // TODO: Implement remove grantee
  }, []);

  const handleNameClick = useCallback(() => {
    // TODO: Implement navigate to user
  }, []);

  const handleActionMenuChange = useCallback(
    (value, userId) => {
      if (value === "change_role") {
        navigate("/v2/user-management");
      } else if (value === "remove_user") {
        const user = localUsers.find(
          (u) => u.userUuid === userId
        );
        if (user) {
          setSelectedUser(user);
          setIsRemoveModalOpen(true);
        }
      }
    },
    [localUsers, navigate]
  );

  const handleCloseRemoveModal = useCallback(() => {
    setIsRemoveModalOpen(false);
    setSelectedUser(null);
  }, []);

  const handleRemoveUser = useCallback(async () => {
    if (!selectedUser || isRemoving) {
      return;
    }

    setIsRemoving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const userName = selectedUser.userFullName || "";

      setLocalUsers((prevUsers) =>
        prevUsers.filter((u) => u.userUuid !== selectedUser.userUuid)
      );

      handleCloseRemoveModal();

      toast.success({
        title: "User removed",
        message: `${userName} has been successfully removed from the department.`,
      });

      if (departmentUuid) {
        fetchUsers({
          department_uuid: departmentUuid,
          page: currentPage,
          page_size: itemsPerPage,
          order_by: ["created_at"],
          order_direction: ["DESC"],
          search: effectiveSearchKeyword.trim()
            ? { keyword: effectiveSearchKeyword.trim() }
            : undefined,
        });
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error({
        title: "Error",
        message: "Failed to remove user. Please try again.",
      });
    } finally {
      setIsRemoving(false);
    }
  }, [
    selectedUser,
    handleCloseRemoveModal,
    isRemoving,
    toast,
    departmentUuid,
    currentPage,
    itemsPerPage,
    effectiveSearchKeyword,
    fetchUsers,
  ]);

  const getRoleColor = useCallback((role) => {
    const roleMap = {
      "Department Admin": "primary",
      "Platform Admin": "informative",
      "Group Manager": "success",
      User: "neutral",
    };
    return roleMap[role] || "neutral";
  }, []);

  const getStatusColor = useCallback((status) => {
    const statusMap = {
      ACTIVE: "success",
      PENDING: "alert",
      INACTIVE: "neutral",
    };
    return statusMap[status] || "neutral";
  }, []);

  const availableRoles = useMemo(() => {
    const roles = [...new Set(localUsers.map((u) => u.memberRole).filter(Boolean))];
    return roles.sort();
  }, [localUsers]);

  const tableData = paginatedUsers.map((user) => {
    // Map fields from new API contract
    const fullName = user.userFullName || "Unknown User";
    const avatarColor = generateAvatarColor(fullName);
    const userUuid = user.userUuid || "";
    const formattedDate = formatDate(user.updatedAt);

    return {
      id: userUuid,
      userName: (
        <AvatarNameContainer>
          <NameInitialsAvatar
            name={fullName}
            size="32px"
            bgColor={avatarColor}
            textColor="#ffffff"
            borderRadius="50%"
            textWeight="bold"
            textSize="12px"
            borderWidth="0"
          />
          <NameCell
            onClick={() =>
              handleNameClick(
                userUuid,
                fullName
              )
            }
          >
            {fullName}
          </NameCell>
        </AvatarNameContainer>
      ),
      email: user.userEmail || "—",
      role: user.memberRole ? (
        <Tag color={getRoleColor(user.memberRole)} size="small" text={user.memberRole} />
      ) : (
        "—"
      ),
      status: user.status ? (
        <Tag
          color={getStatusColor(user.status)}
          size="small"
          text={user.status}
        />
      ) : (
        "—"
      ),
      groups: (() => {
        const list = user.groups?.filter((g) => g?.groupName);
        if (!list?.length) return "—";
        return (
          <GroupsContainer>
            {list.map((g) => (
              <Tag key={g.groupUuid || g.groupName} color="secondary" size="small" text={g.groupName} />
            ))}
          </GroupsContainer>
        );
      })(),
      lastActive: formattedDate,
      joined: formattedDate,
      actions: (
        <ButtonMenu
          customIcon="dotsMenu"
          disableEffects
          iconPosition="right"
          label=""
          menuPosition="bottom"
          alignMenu="right"
          onChange={(value) =>
            handleActionMenuChange(
              value,
              userUuid
            )
          }
          onClick={(e) => e.stopPropagation()}
          size="medium"
          type="tertiary"
          width="auto"
        >
          <ButtonMenu.Menu icon={true}>
            <ButtonMenu.Item
              label="Change Role"
              value="change_role"
              icon="shield"
            />
            <ButtonMenu.Item
              label="Remove User"
              value="remove_user"
              icon="person"
            />
          </ButtonMenu.Menu>
        </ButtonMenu>
      ),
    };
  });

  const headers = [
    { label: "User name", value: "userName" },
    { label: "Email", value: "email" },
    { label: "Role", value: "role" },
    { label: "Status", value: "status" },
    { label: "Groups", value: "groups" },
    { label: "Last Active", value: "lastActive" },
    { label: "Joined", value: "joined" },
    { label: "Actions", value: "actions" },
  ];

  const selectedRoleValue = selectedRole
    ? { value: selectedRole, label: selectedRole }
    : undefined;

  const selectedStatusValue = selectedStatus
    ? { value: selectedStatus, label: formatStatusLabel(selectedStatus) }
    : undefined;

  const totalPages =
    meta?.totalPages || Math.ceil(filteredUsers.length / itemsPerPage);

  return (
    <UsersContainer>
      <HighlightCardsContainer>
        <HighlightCardColumn>
          <HighlightCards
            variant="lightBlue"
            label="Total Users"
            value={statistics.totalUsers.toString()}
            icon="person"
            showIcon
          />
        </HighlightCardColumn>
        <HighlightCardColumn>
          <HighlightCards
            variant="green"
            label="Active"
            value={statistics.activeUsers.toString()}
            icon="person"
            showIcon
          />
        </HighlightCardColumn>
        <HighlightCardColumn>
          <HighlightCards
            variant="orange"
            label="Pending"
            value={statistics.pendingUsers.toString()}
            icon="person"
            showIcon
          />
        </HighlightCardColumn>
        <HighlightCardColumn>
          <HighlightCards
            variant="purple"
            label="Admins"
            value={statistics.adminUsers.toString()}
            icon="person"
            showIcon
          />
        </HighlightCardColumn>
      </HighlightCardsContainer>

      <FiltersContainer>
        <FiltersLeftContent>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search users by name or email..."
            value={searchKeyword}
            disabled={loading}
          />

          <DropdownWrapper>
            <Select
              key={`role-filter-${filtersKey}`}
              label="Role"
              placeholder="All Roles"
              onChange={handleRoleChange}
              size="small"
              value={selectedRoleValue}
              width="auto"
              minWidth="120px"
            >
              <Select.Menu>
                <Select.Item value={null} label="All Roles">
                  All Roles
                </Select.Item>
                {availableRoles.map((role) => (
                  <Select.Item key={role} value={role} label={role}>
                    {role}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
          </DropdownWrapper>

          <DropdownWrapper>
            <Select
              key={`status-filter-${filtersKey}`}
              label="Status"
              placeholder="All Status"
              onChange={handleStatusChange}
              size="small"
              value={selectedStatusValue}
              width="auto"
              minWidth="120px"
            >
              <Select.Menu>
                <Select.Item value={null} label="All Status">
                  All Status
                </Select.Item>
                {statusOptions.map((statusOption) => (
                  <Select.Item
                    key={statusOption.value}
                    value={statusOption.value}
                    label={statusOption.label}
                  >
                    {statusOption.label}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
          </DropdownWrapper>

          {hasActiveFilters && (
            <Button
              label="Clear Filters"
              size="medium"
              type="secondary"
              onClick={handleClearFilters}
              disabled={loading}
            />
          )}
        </FiltersLeftContent>

        <FiltersRightContent>
          <Button
            iconLeft="plus"
            label="Add User"
            size="medium"
            type="primary"
            onClick={handleAddUser}
          />
        </FiltersRightContent>
      </FiltersContainer>

      <UsersTableSection
        tableData={tableData}
        headers={headers}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta?.totalItems || filteredUsers.length}
        totalPages={totalPages}
        loading={loading}
        error={error}
        isEmpty={isEmpty}
      />

      <RemoveUserModal
        isOpen={isRemoveModalOpen}
        onClose={handleCloseRemoveModal}
        onConfirm={handleRemoveUser}
        userName={selectedUser?.userFullName || ""}
        isLoading={isRemoving}
      />

      <AddUserModal
        isOpen={isAddUserModalOpen}
        store={addUserModalStore}
        resourcesStore={resourcesStore}
        onClose={handleCloseAddUserModal}
        onShare={handleShareAll}
        onAddToPending={handleAddToPending}
        onUpdatePendingPermission={handleUpdatePendingPermission}
        onRemovePending={handleRemovePending}
        onUpdateGranteePermission={handleUpdateGranteePermission}
        onRemoveGrantee={handleRemoveGrantee}
        usersList={autocompleteUsers}
        onSearchUsers={handleModalSearchUsers}
        loadingUsers={loadingUsersForModal}
      />
    </UsersContainer>
  );
};

export default Users;
